import type { Route, Location, VueRouter } from 'vue-router/types/router';
import { clearNavigationParams, sideBarItems } from '@mimir/web/store/initialValues';
import { getRouterLink } from '@mimir/client-common/util/basicRoute';
import { ItemType, type SearchItemType } from '@mimir/common/dto/itemTypes';
import { openURLInDefaultBrowser } from '../premiere/cep';
import type { StoreType } from '../store/state';
import { RouteName } from '../constants';
import noop from 'lodash/noop';
import type { DefaultFilters } from '@mimir/common/filters/Filters';
import { isInAdobeApp, type ApplicationContext } from '@mimir/common/context/applicationContext';
import { checkValidMimirURl } from '@mimir/common/url/urlValidation';
import { MEDIA_FILTER, MediaTypes, setRadioFilter } from '../globals/filter';
import type { TenantConfigDto } from '@mimir/common/dto/tenantModel';
import type { User } from '@mimir/web/model/User';
import { tryNavigateToDefaultBookmark } from './bookmarkTree';
import type { ViewType } from '../pages/search/constants';

const routeMatch = (routeName: string) => (route: Route) =>
  route.matched.some(({ name }) => name === routeName);

const routeMatchByPath = (routePath: string) => (route: Route) =>
  route.matched.some(({ path }) => path === routePath);

export const HOME = 'searchItem';

export const toPublicPage = routeMatch('public');

export const toBarePage = routeMatch('bare');
export const toMosPage = routeMatch('mos');

export const toAllTenantsFeatures = routeMatchByPath('/admin');
export const toAdminFeatures = routeMatchByPath('/settings');
export const toFolders = routeMatchByPath(sideBarItems.Folders.routePath);
export const toMyOrganization = routeMatchByPath('/my-organisation');
export const toDesktopSettings = routeMatchByPath('/desktop-settings');
export const toSupportTeamFeatures = routeMatchByPath('/support');

export const getVersionIdInQueryParam = (route: Route) => route.query['versionId'];

export const openInNewTab = (applicationContext: ApplicationContext, itemId: string, lastSearchParams: Record<string, string> = {}, router: VueRouter, itemType: SearchItemType) => {
  const url = getUrlForItem(itemId, lastSearchParams, router, itemType);
  openExternalLink(applicationContext, url);
};

export const openFolderInNewTab = (applicationContext: ApplicationContext, folderId: string, router: VueRouter) => {
  const url = getUrlForFolder(folderId, router);
  openExternalLink(applicationContext, url);
};

export const getUrlForItem = (itemId: string, lastSearchParams: Record<string, string> | undefined, router: VueRouter, itemType: SearchItemType) => {
  const routerLink = getRouterLink(itemType, { id: itemId }, { ...lastSearchParams });
  const urlPath = router.resolve(routerLink).href;
  return `${window.location.origin}${urlPath}`;
};

export const getUrlForFolder = (folderId: string, router: VueRouter) => {
  const routerLink = getRouterLink(ItemType.FOLDER, {}, { folderId });
  const urlPath = router.resolve(routerLink).href;
  return `${window.location.origin}${urlPath}`;
};

export const generateRouterLink = (itemId: string, lastSearchParams: Record<string, string> = {}, itemType: SearchItemType, cutterRoute = false) => {
  const routerParams: Record<string, string> = { id: itemId };
  const routerQuery: Record<string, string> = { time: '0', ...lastSearchParams, reload: 'false' };
  if (routerQuery.folderId) routerQuery.returnTo = 'folderView';
  if (cutterRoute) {
    if (itemType === ItemType.CLIP_LIST || itemType === ItemType.TIMELINE) return { name: 'sequenceProperties', params: { ...routerParams, sequenceId: itemId }, query: routerQuery };
    return { name: 'itemProperties', params: { ...routerParams, itemId }, query: routerQuery };
  }

  return getRouterLink(itemType, routerParams, routerQuery);
};

export const openExternalLink = (applicationContext: ApplicationContext, url: string) => {
  if (isInAdobeApp(applicationContext)) {
    openURLInDefaultBrowser(url);
  } else {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
};

/**
 * Add current query parameters to location
 */
export const addQueryParams = (router: VueRouter, location: Location): Location  => {
  const { query } = location;
  return { ...location, query: { ...router.currentRoute.query, ...query } };
};

/**
 * Navigate to default search with standard filters
 */
const navigateToDefaultSearch = (router: VueRouter, store: StoreType, activeTab?: string) => {
  if (store.getters.isMimirBrowse as boolean) {
    return void router.push({ name: RouteName.BROWSE }).catch(noop);
  }

  const isDefaultAll = (store.getters.tenantConfig as TenantConfigDto).showAllItemTypesInDefaultSearch ?? false;
  const name = activeTab === 'cutter' ? 'sourceBrowser' : 'searchItem';
  if (isDefaultAll) {
    void router.push({
      name,
      query: { ts: new Date().getTime().toString(), allTypes: 'true' },
    }).catch(noop);
    return;
  }

  const defaultFilters = store.getters.filters.defaultFilters as DefaultFilters;
  setRadioFilter(MEDIA_FILTER);
  MediaTypes.forEach((type) => {
    if (type === 'sequence') {
      defaultFilters['filter:itemType'].addValue('clipList');
      defaultFilters['filter:itemType'].addValue('timeline');
    } else {
      defaultFilters['filter:itemType'].addValue(type);
    }
  });
  const typeFilterValue = defaultFilters['filter:itemType'].serializedValue;
  void router.push({
    name,
    query: typeFilterValue ? { searchString: typeFilterValue,  ts: new Date().getTime().toString() } : { ts: new Date().getTime().toString() },
  }).catch(noop);
};

export const handleLogoOrResetFilterClick = async (router: VueRouter, store: StoreType) => {
  // Check if user's primary group has a default bookmark and navigate to it
  const user = store.getters.user as User;
  const navigatedToBookmark = await tryNavigateToDefaultBookmark(user);

  if (!navigatedToBookmark) {
    // Default behavior: navigate to search with default filters after clearing all params
    await clearNavigationParams(store);

    navigateToDefaultSearch(router, store);
  }
};

/**
 * Handle reset button click - completely reset everything (remove all filters, folder selection and search terms)
 */
export const handleResetFilterClick = async (router: VueRouter, store: StoreType, activeTab: string) => {
  await clearNavigationParams(store);

  // Reset to default search behavior without any bookmarks
  navigateToDefaultSearch(router, store, activeTab);
};

export async function navigateToMimirUrl(url: string, routerObj: VueRouter) {
  if (!checkValidMimirURl(url, window.location.host)) return;

  const navigationURLObject = new URL(url);
  if (navigationURLObject.pathname.startsWith('/shares/')) {
    // shares/{sharedId} isn't a route but a different endpoint
    location.href = url;
    return;
  }

  await routerObj.push(`${navigationURLObject.pathname}?${navigationURLObject.searchParams}`);
}
export const onFilterMenuClicked = async (router: VueRouter, store: StoreType, viewType: ViewType) => {
  const defaultFilters = store.getters.filters.defaultFilters as DefaultFilters;

  await clearNavigationParams(store);
  setRadioFilter(MEDIA_FILTER);
  MediaTypes.forEach((type) => {
    if (type === 'sequence') {
      defaultFilters['filter:itemType'].addValue('clipList');
      defaultFilters['filter:itemType'].addValue('timeline');
    } else {
      defaultFilters['filter:itemType'].addValue(type);
    }
  });
  const typeFilterValue = defaultFilters['filter:itemType'].serializedValue;
  void router.push({
    name: 'searchItem',
    query: { searchString: typeFilterValue, viewType },
  }).catch(noop);
};

